/**
 * O tier de dificuldade de cada área da Season 1.
 *
 * <p><b>Ele é calculado, não guardado.</b> A curva que o Theo desenhou é uma função da
 * distância até onde o jogador nasce, e escrever os 21 números à mão seria guardar o
 * <i>resultado</i> de uma regra em vez da regra — a mesma armadilha da régua de
 * progresso, que já disse 16/49 contra 16/21.
 *
 * <p>Calcular tem uma consequência boa: mover uma área no mapa reposiciona o tier dela
 * sozinho, e a tela nunca fica dizendo T4 sobre um hexágono que virou vizinho do
 * nascimento.
 *
 * <p>A regra, na ordem em que decide:
 *
 * <ol>
 *   <li>O <b>nascimento</b> é o vértice onde as três regiões da Season se tocam. Ele é a
 *       média dos centros das três — não é palpite: com as regiões nos slots 0, 1 e 2,
 *       essa média cai exatamente sobre o vértice compartilhado.</li>
 *   <li>As três áreas <b>encostadas nesse vértice</b> são T1. São elas que o jogador
 *       escolhe no minuto zero.</li>
 *   <li>O <b>centro de cada região</b> é T2 — a segunda parada de quem entrou ali.</li>
 *   <li>O resto é T3, T4 e T5, <b>por distância</b> ao nascimento: as duas mais perto de
 *       cada região são T3, as duas seguintes T4, a mais longe T5.</li>
 * </ol>
 */
import { centroDaArea } from '../mundo/geometriaDoMapa';
import type { RegiaoNoMapa } from '../mundo/tipos';

/** Quantas regiões a Season 1 cobre — os slots 0, 1 e 2 do mapa. */
export const REGIOES_DA_SEASON = 3;

/** Quantas áreas de cada tier uma região tem. É o mesmo perfil nas três. */
export const PERFIL_DA_REGIAO = [1, 2, 3, 3, 4, 4, 5];

export interface AreaComTier {
  areaId?: number;
  nome: string;
  /** Slot da área dentro da região (0 é o centro). */
  slot: number;
  regiao: string;
  regiaoSlot: number;
  cor: string;
  tier: number;
  x: number;
  y: number;
  /** Distância até o nascimento, em unidades do SVG. */
  distancia: number;
}

export interface MapaDaSeason {
  areas: AreaComTier[];
  nascimento: { x: number; y: number };
  regioes: { nome: string; slot: number; cor: string }[];
}

const COR_PADRAO = '#8e8875';

/**
 * Monta a Season a partir do mapa que a API devolve.
 *
 * <p>Regiões fora dos três primeiros slots são ignoradas, e o mesmo vale para hexágono
 * sem área: a Season é um recorte, e slot vazio não é área de dificuldade nenhuma.
 */
export function mapaDaSeason(regioes: RegiaoNoMapa[]): MapaDaSeason {
  const daSeason = regioes
    .filter((r) => r.indiceMapa < REGIOES_DA_SEASON)
    .sort((a, b) => a.indiceMapa - b.indiceMapa);

  const nascimento = mediaDosCentros(daSeason.map((r) => r.indiceMapa));

  const areas: AreaComTier[] = [];

  for (const regiao of daSeason) {
    const cor = regiao.corMapa || COR_PADRAO;
    const preenchidos = regiao.hexagonos
      .filter((h) => h.nome)
      .map((h) => {
        const c = centroDaArea(regiao.indiceMapa, h.indiceMapa);
        return {
          areaId: h.areaId,
          nome: h.nome as string,
          slot: h.indiceMapa,
          regiao: regiao.nome ?? '—',
          regiaoSlot: regiao.indiceMapa,
          cor,
          x: c.x,
          y: c.y,
          distancia: Math.hypot(c.x - nascimento.x, c.y - nascimento.y),
          tier: 0,
        };
      });

    /*
     * O centro sai da fila antes da ordenação: ele é T2 por papel, não por distância.
     * Deixá-lo na fila o colocaria entre os T3, porque ele fica mais longe do
     * nascimento que a pétala encostada nele.
     */
    const centro = preenchidos.find((a) => a.slot === 0);
    if (centro) centro.tier = 2;

    const fila = preenchidos.filter((a) => a.slot !== 0).sort((a, b) => a.distancia - b.distancia);
    const ESCALA = [1, 3, 3, 4, 4, 5];
    fila.forEach((area, i) => {
      area.tier = ESCALA[Math.min(i, ESCALA.length - 1)];
    });

    areas.push(...preenchidos);
  }

  return {
    areas,
    nascimento,
    regioes: daSeason.map((r) => ({
      nome: r.nome ?? '—',
      slot: r.indiceMapa,
      cor: r.corMapa || COR_PADRAO,
    })),
  };
}

/**
 * O ponto onde as regiões se tocam: a média dos centros delas.
 *
 * <p>Com os três slots da Season, esse ponto cai sobre o vértice compartilhado pelas
 * três áreas de T1 — é o que faz o nascimento não precisar de coordenada escrita.
 */
function mediaDosCentros(slots: number[]): { x: number; y: number } {
  if (!slots.length) return { x: 0, y: 0 };
  const centros = slots.map((s) => centroDaArea(s, 0));
  return {
    x: centros.reduce((soma, c) => soma + c.x, 0) / centros.length,
    y: centros.reduce((soma, c) => soma + c.y, 0) / centros.length,
  };
}
